import { h, Fragment, FunctionComponent } from "preact";
import { Page } from "../layout/page";

import css from "./resume_page.scss";

export const ResumePage: FunctionComponent = () => {
  const experienceItems = EXPERIENCES.map((exp) => (
    <li>
      <div class={css("experience-head")}>
        <h3>{exp.company}</h3>
        <div class={css("team")}>{exp.team}</div>
        <div class={css("time")}>
          {exp.from} — {exp.to}
        </div>
      </div>
      <div class={css("details")}>{exp.details}</div>
    </li>
  ));

  return (
    <Page showHeader={false}>
      <div class={css("resume")}>
        <div class={css("header")}>
          <div class={css("id")}>
            <h1>
              <a href="/">Saeed Jahed</a>
            </h1>
            Software Engineer
          </div>
          <div class={css("contact")}>
            <div>
              <a href="mailto:saeed@netio.ca">saeed@netio.ca</a>
            </div>
            <div>Seattle WA</div>
          </div>
        </div>

        <div class={css("section")}>
          <div class={css("section-head")}>
            <h2>Experience</h2>
          </div>
          <ul class={css("section-content", "experiences")}>{experienceItems}</ul>
        </div>

        <div class={css("section")}>
          <div class={css("section-head")}>
            <h2>Technology</h2>
          </div>

          <div class={css("section-content", "skill-sets")}>
            {SKILL_SETS.map((set) => (
              <div class={css("skills")}>
                <h3>{set.title}</h3>
                <ul>
                  {set.skills.map((skill) => (
                    <li>{skill}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div class={css("section")}>
          <div class={css("section-head")}>
            <h2>Volunteering</h2>
          </div>
          <ul class={css("section-content", "volunteering")}>
            <li>
              <h3>
                <a href="https://code.likeagirl.io/my-summer-in-the-google-codeu-engineering-program-475666b322a2">
                  Google CodeU
                </a>
              </h3>
              <div class={css("team")}>Project Advisor</div>
            </li>
            <li>
              <h3>
                <a href="https://www.desc.org/">DESC</a>
              </h3>
              <div class={css("team")}>Software</div>
            </li>
            <li>
              <h3>
                <a href="https://www.helpinglink.org/">Helping Link</a>
              </h3>
              <div class={css("team")}>Web Developer</div>
            </li>
          </ul>
        </div>

        <div class={css("section")}>
          <div class={css("section-head")}>
            <h2>Education</h2>
          </div>
          <div class={css("section-content", "education")}>
            <div class={css("title")}>Honours B.Sc., Computer Science</div>
            <div class={css("school")}>McMaster University, Canada</div>
            <div class={css("date")}>2007</div>
          </div>
        </div>
      </div>
    </Page>
  );
};

const EXPERIENCES = [
  {
    company: "Google",
    team: "Meet + Hangouts",
    from: "February 2017",
    to: "present",
    details: (
      <>
        Provided technical leadership to a large engineering team, making rapid and high impact
        infrastructural changes to <a href="https://meet.google.com/">Google Meet</a> to{" "}
        <a href="https://cloud.google.com/blog/products/g-suite/keeping-google-meet-ahead-of-usage-demand-during-covid-19">
          scale
        </a>{" "}
        the video calling technology in the face of{" "}
        <a href="https://www.wired.com/story/how-google-meet-weathered-work-from-home-explosion/">
          extreme usage surge
        </a>{" "}
        during the COVID-19 pandemic.
        <p />
        Brought first class native support for Meet and Hangouts across all major browsers and
        established highly productive relationships between Google and{" "}
        <a href="https://9to5google.com/2020/02/10/hangouts-meet-safari-support/">Apple</a>,
        Microsoft, and{" "}
        <a href="https://blog.mozilla.org/webrtc/firefox-is-now-supported-by-google-hangouts-and-meet/">
          Mozilla
        </a>{" "}
        to fix and maintain WebRTC and media APIs and allow large-scale multi-way video calling on
        the web.
        <p />
        Directly responsible for maintaining stability of critical integration points for Meet on
        web (UI, backend, and WebRTC), having led and influenced many high impact projects that
        touch many surfaces as a tech lead and by serving as a member of the org-wide design
        committee.
      </>
    ),
  },
  {
    company: "Microsoft",
    team: "Research",
    from: "May 2013",
    to: "September 2016",
    details: (
      <>
        Member of technical staff and founding member of the moonshot-focused{" "}
        <a href="https://www.zdnet.com/article/microsoft-research-special-projects-group-tipped-to-take-on-google-x/">
          Special Projects
        </a>{" "}
        team at MSR. Successfully pitched and led multiple projects leveraging MSR's machine
        learning research in areas such as mobile human interfaces and peer-to-peer file sharing.
        <p />
        Externalized technologies from MSR's computational photography group to ship the first
        version of the Microsoft{" "}
        <a href="https://apps.apple.com/us/app/microsoft-pix/id1127910488">Pix</a> camera app for
        the iPhone. Provided technical leadership to a team of engineers and worked closely with UX
        and interaction designers.
        <p />
        Designed, developed, and shipped the Microsoft{" "}
        <a href="https://play.google.com/store/apps/details?id=com.microsoft.hyperlapsemobile">
          Hyperlapse
        </a>{" "}
        app for Android with over one million downloads in its first month.
        <p />
      </>
    ),
  },
  {
    company: "Microsoft",
    team: "HealthVault",
    from: "August 2008",
    to: "May 2013",
    details: (
      <>
        Responsible for improving and maintaining the performance of the{" "}
        <a href="https://en.wikipedia.org/wiki/Microsoft_HealthVault">HealthVault</a> platform.
        Implemented a high­ performance highly ­scalable caching service to improve the performance
        of retrieval operations. Created performance regression detection tooling that caught and
        prevented many critical performance issues.
        <p />
        Made critical architectural decisions for the entire service around cross­-ocean
        connectivity that ultimately lead to improved service quality for the HealthVault
        application ecosystem.
        <p />
        Created input fuzzing framework for testing complex backwards compatibility scenarios
        between different versions of APIs exposed by the HealthVault platform.
      </>
    ),
  },
];

const SKILL_SETS = [
  {
    title: "Web",
    skills: ["JavaScript + TypeScript", "Webpack + Closure", "HTML + CSS", "React"],
  },
  {
    title: "Mobile",
    skills: ["iOS (Swift + SwiftUI)", "Xamarin + Cordova", "React Native", "Android"],
  },
  {
    title: "Backend",
    skills: ["CouchDB + Mongo", "SQL Server", "NodeJS", "Redis", ".NET"],
  },
];
